import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useKiosk } from '../../contexts/KioskContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../ui/Button'
import toast from 'react-hot-toast'

const { FiLock, FiX, FiUnlock, FiAlertTriangle } = FiIcons

const ParentPinModal = ({ isOpen, onClose, onSuccess }) => {
  const { validateParentPin } = useKiosk()
  const [pin, setPin] = useState('')
  const [loading, setLoading] = useState(false)
  const [attempts, setAttempts] = useState(0)
  const maxAttempts = 3

  const handleSubmit = (e) => {
    e.preventDefault()
    
    if (!pin) {
      toast.error('Please enter your PIN')
      return
    }

    if (attempts >= maxAttempts) {
      toast.error('Too many failed attempts. Please refresh the page.')
      return
    }

    setLoading(true)

    // Simulate a small delay for security
    setTimeout(() => {
      if (validateParentPin(pin)) {
        toast.success('PIN verified! Exiting kiosk mode...')
        setPin('')
        setAttempts(0)
        onSuccess()
      } else {
        const newAttempts = attempts + 1
        setAttempts(newAttempts)
        toast.error(`Incorrect PIN. ${maxAttempts - newAttempts} attempts remaining.`)
        setPin('')
        
        if (newAttempts >= maxAttempts) {
          toast.error('Maximum attempts reached. Please refresh the page to try again.')
        }
      }
      setLoading(false)
    }, 500)
  }

  const handleClose = () => {
    if (attempts >= maxAttempts) {
      toast.error('Cannot close - too many failed attempts. Please refresh the page.')
      return
    }
    setPin('')
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md border border-gray-200 dark:border-gray-700"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiLock} className="text-red-600 dark:text-red-400 text-xl" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Parent Verification</h2>
            </div>
            {attempts < maxAttempts && (
              <Button variant="ghost" size="sm" onClick={handleClose} className="p-2">
                <SafeIcon icon={FiX} className="text-xl" />
              </Button>
            )}
          </div>

          <div className="text-center mb-6">
            <p className="text-gray-600 dark:text-gray-400">
              Enter your parent PIN to exit kiosk mode and access parent controls.
            </p>
            {attempts > 0 && (
              <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <div className="flex items-center justify-center gap-2 text-yellow-800 dark:text-yellow-300">
                  <SafeIcon icon={FiAlertTriangle} className="text-sm" />
                  <span className="text-sm">
                    {attempts}/{maxAttempts} failed attempts
                  </span>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Parent PIN
              </label>
              <input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-center text-2xl font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="••••"
                autoFocus
                maxLength={10}
                disabled={attempts >= maxAttempts}
              />
            </div>

            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                <strong>Security:</strong> This PIN was set when you enabled kiosk mode.
                {attempts >= maxAttempts 
                  ? ' Please refresh the page to reset attempts.'
                  : ' If you\'ve forgotten it, you\'ll need to refresh the page and sign in again.'
                }
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={handleClose}
                className="flex-1"
                disabled={loading || attempts >= maxAttempts}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                className="flex-1"
                loading={loading}
                disabled={!pin || attempts >= maxAttempts}
              >
                <SafeIcon icon={FiUnlock} className="text-sm" />
                Verify PIN
              </Button>
            </div>
          </form>

          {/* Help Text */}
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              {attempts >= maxAttempts 
                ? 'Refresh the page to reset and try again.'
                : 'Forgot your PIN? Refresh the page and sign in with your account.'
              }
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default ParentPinModal